import { Typography } from '@mui/material';
import React, { FC, useId } from 'react';

import { FILE__MAX_FILES_UPLOAD } from '../../../constants/forms';
import useIsMobile from '../../../hooks/useIsMobile';
import translate from '../../../i18n/translate';
import { InputFileProps } from '../../../models/attachment';

import InputFileAttachment, {
  Props as InputAttachmentProps,
} from './InputFileAttachment';
import * as Styled from './styled';

type Props = {
  attachmentsListLength: number;
  inputFileProps: InputFileProps;
};
const MAX_NUMBER_OF_ATTACHMENTS = FILE__MAX_FILES_UPLOAD - 1;

/**
 * Section for uploading attachments
 *
 * @param props                       Props passed to the component
 * @param props.attachmentsListLength The number of attachments in the list
 * @param props.inputFileProps        Props passed to the <input type="file" />
 *
 * @returns                           The component itself
 */
const AttachmentUploadSection: FC<Props> = ({
  attachmentsListLength,
  inputFileProps,
}) => {
  const inputId = useId();
  const isEmpty = attachmentsListLength === 0;
  const isMobile = useIsMobile();

  const inputAttachmentProps: InputAttachmentProps = {
    children: isMobile
      ? null
      : translate(isEmpty ? 'SELECT__FILES' : 'FILES__ADD_MORE'),
    inputFileProps,
    inputId,
    isDisabled: attachmentsListLength > MAX_NUMBER_OF_ATTACHMENTS,
  };

  const children = <InputFileAttachment {...inputAttachmentProps} />;

  if (isMobile) {
    return children;
  }

  if (isEmpty === false) {
    return (
      <Styled.SectionInline>
        {children}
        <Typography variant="caption">
          {translate('FILES__MAXIMUM_SIZE')}
        </Typography>
      </Styled.SectionInline>
    );
  }

  return (
    <Styled.Section>
      <Styled.Title variant="body1">
        {translate('FILES__CHOOSE_UPLOAD')}
      </Styled.Title>
      {children}
      <Typography variant="caption">
        {translate('FILES__MAXIMUM_SIZE')}
      </Typography>
    </Styled.Section>
  );
};

export default AttachmentUploadSection;
